import React, { useState } from 'react';
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signOut,
} from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../firebase';
import { allowed_domains } from '../auth/allowedDomains';
import './Login.css';

// We don't set an `hd` Google account-chooser hint: it only accepts a single
// Workspace domain, and we now allow more than one. The allowed domains are
// enforced below on the client and, authoritatively, by the Go backend on
// every request (see server/auth/firebase.go).
const googleProvider = new GoogleAuthProvider();

function isPasswordOk(pwd) {
  const pwdReqs = 'Password must be at least 8 characters, have 1 capital, 1 number, and 1 special character';
  if (pwd.length < 8) {
    alert(pwdReqs);
    return false;
  }
  if (!pwd.match(/[A-Z]/) || !/\d/.test(pwd)) {
    alert(pwdReqs);
    return false;
  }
  return true;
}

export default function Login() {
  const [userEmail, setUserEmail] = useState('');
  const [userPass, setUserPass] = useState('');
  const [buttonsDisabled, setButtonsDisabled] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const emailRegex = /[a-zA-Z0-9]+@[a-zA-Z]+\.[a-z]{2,3}/;

  onAuthStateChanged(auth, (user) => {
    if (user && user.emailVerified) {
      navigate('/home');
    }
  });

  const handleSignup = () => {
    navigate('/signup');
  };

  const handleSignIn = () => {
    setButtonsDisabled(true);
    setLoading(true);

    const domain = userEmail.split('@')[1];
    if (!emailRegex.test(userEmail) || !allowed_domains.includes(domain)) {
      setButtonsDisabled(false);
      setLoading(false);
      alert('Email Invalid');
      return;
    }
    if (!isPasswordOk(userPass)) {
      setButtonsDisabled(false);
      setLoading(false);
      return;
    }

    signInWithEmailAndPassword(auth, userEmail, userPass)
      .then(() => {
        if (!auth.currentUser.emailVerified) {
          sendEmailVerification(auth.currentUser);
          alert('Please check your inbox for an email verification');
        }
      })
      .catch((error) => {
        alert('Unable to sign in: ' + error.message);
      })
      .finally(() => {
        setButtonsDisabled(false);
        setLoading(false);
      });
  };

  const handleGoogleSignIn = () => {
    setButtonsDisabled(true);
    setLoading(true);

    signInWithPopup(auth, googleProvider)
      .then((result) => {
        const domain = (result.user.email || '').split('@')[1];
        if (!allowed_domains.includes(domain)) {
          signOut(auth);
          alert('Please sign in with an approved Google account (' + allowed_domains.join(' or ') + ').');
        }
        // onAuthStateChanged below handles the redirect to /home, which sends
        // them on to the first section their profile allows.
      })
      .catch((error) => {
        if (error.code !== 'auth/popup-closed-by-user') {
          alert('Unable to sign in with Google: ' + error.message);
        }
      })
      .finally(() => {
        setButtonsDisabled(false);
        setLoading(false);
      });
  };

  const sendPasswordReset = () => {
    setButtonsDisabled(true);
    setLoading(true);

    const domain = userEmail.split('@')[1];
    if (!emailRegex.test(userEmail) || !allowed_domains.includes(domain)) {
      setButtonsDisabled(false);
      setLoading(false);
      alert('Email Invalid');
      return;
    }

    sendPasswordResetEmail(auth, userEmail)
      .then(() => {
        alert('Please check your inbox for a reset link');
      })
      .catch((error) => {
        alert('Unable to send reset link: ' + error.message);
      })
      .finally(() => {
        setButtonsDisabled(false);
        setLoading(false);
      });
  };

  return (
    <div className="login-container">
      {loading && (
        <div className="loading-overlay">Loading…</div>
      )}

      <div className="login-card">
        <div className="logo-wrapper">
          <span className="logo-text">TAX TITANS</span>
        </div>

        <button
          type="button"
          className="btn google-btn"
          disabled={buttonsDisabled}
          onClick={handleGoogleSignIn}
        >
          <span className="google-icon" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
              <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" />
              <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" />
              <path fill="#FBBC05" d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z" />
              <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" />
            </svg>
          </span>
          Sign in with Google
        </button>

        <div className="divider"><span>or</span></div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSignIn();
          }}
        >
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              onChange={(e) => setUserEmail(e.target.value)}
              name="email"
              type="email"
              placeholder="you@taxtitansusa.com"
            />
          </div>

          <div className="form-group">
            <label htmlFor="pwd">Password</label>
            <input
              onChange={(e) => setUserPass(e.target.value)}
              name="pwd"
              type="password"
              placeholder="••••••••"
            />
          </div>

          <button
            type="submit"
            disabled={buttonsDisabled}
            className="btn primary-btn"
          >
            Login
          </button>
        </form>

        <div className="actions">
          <button
            className="btn secondary-btn"
            disabled={buttonsDisabled}
            onClick={handleSignup}
          >
            Sign Up
          </button>
          <p className="reset-link" onClick={sendPasswordReset}>
            Reset Password
          </p>
        </div>
      </div>
    </div>
  );
}
